import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Home, Languages } from 'lucide-react';
import type { TenantBrand } from '@tendedloop/shared';
import { BrandBand } from '@/components/BrandBand';
import { applyBrand, useBrand } from '@/lib/brand';
import type { TourLang, TourStrings } from './strings';

/**
 * The frame of the public tour pages: the building's band (the same component
 * as the portal's sign-in), or TendedLoop's mark when there is no brand to
 * show, the language switch, and the page's language and direction.
 * The page owns its palette while open and hands it back on leaving.
 */
export function TourChrome({
  brand,
  loading,
  lang,
  s,
  otherLangHref,
  title,
  children,
}: {
  brand: TenantBrand | null;
  loading: boolean;
  lang: TourLang;
  s: TourStrings;
  otherLangHref: string;
  title: string;
  children: ReactNode;
}) {
  const sessionBrand = useBrand((st) => st.brand);
  useEffect(() => {
    if (!loading) applyBrand(brand);
  }, [brand, loading, sessionBrand]);
  useEffect(() => () => applyBrand(useBrand.getState().brand), []);

  useEffect(() => {
    const html = document.documentElement;
    html.lang = lang;
    html.dir = s.dir;
    return () => {
      html.lang = 'he';
      html.dir = 'rtl';
    };
  }, [lang, s.dir]);

  useEffect(() => {
    const prev = document.title;
    document.title = brand?.displayName ? `${title} · ${brand.displayName}` : title;
    return () => {
      document.title = prev;
    };
  }, [title, brand?.displayName]);

  return (
    <div dir={s.dir} lang={lang} className="min-h-screen bg-white text-slate-800">
      {loading ? (
        <div className="mb-6" aria-hidden data-testid="band-skeleton">
          <div className="flex min-h-[240px] animate-pulse items-center justify-center bg-slate-100 px-6 pb-10 pt-12">
            <div className="h-16 w-[200px] max-w-full rounded-xl bg-slate-200" />
          </div>
        </div>
      ) : brand ? (
        <BrandBand brand={brand} size="hero" />
      ) : (
        <div className="flex flex-col items-center px-6 pb-4 pt-12" data-brand-level="none">
          <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-500 text-white shadow-lg">
            <Home className="h-8 w-8" />
          </div>
          <p className="text-lg font-bold tracking-tight text-brand-900" dir="ltr">
            TendedLoop
          </p>
        </div>
      )}
      <main className="mx-auto w-full max-w-md px-4 pb-16">
        <div className="mb-2 flex justify-end">
          <Link
            to={otherLangHref}
            replace
            lang={s.otherLang.lang}
            className="flex min-h-[48px] items-center gap-1.5 rounded-xl px-3 text-[16px] font-semibold text-brand-700"
          >
            <Languages className="h-5 w-5" aria-hidden />
            {s.otherLang.label}
          </Link>
        </div>
        {children}
      </main>
    </div>
  );
}

/** A centred message card (not found, sent, cancelled...). */
export function TourMessage({
  icon,
  title,
  children,
  tone = 'neutral',
}: {
  icon: ReactNode;
  title: string;
  children?: ReactNode;
  tone?: 'neutral' | 'error';
}) {
  return (
    <div
      className={
        'rounded-2xl border bg-white p-6 text-center shadow-sm ' +
        (tone === 'error' ? 'border-red-100' : 'border-slate-100')
      }
      role={tone === 'error' ? 'alert' : 'status'}
    >
      <div className="mx-auto mb-3 flex justify-center">{icon}</div>
      <h1 className="mb-2 text-xl font-bold text-brand-900">{title}</h1>
      {children}
    </div>
  );
}
